import { t } from "@lingui/core/macro";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { toast } from "sonner";

import type { AiAction } from "@/integrations/multiresume/quota/types";

import { useAuthSession } from "@/hooks/use-auth-session";
import { useConfirm } from "@/hooks/use-confirm";
import {
  getQuotaConfirmationActionClass,
  shouldPromptForQuotaAction,
  writeQuotaConfirmationPreference,
} from "@/integrations/multiresume/quota/confirmation-preferences";
import { client } from "@/integrations/orpc/client";

type QuotaPreflightRunOptions = {
  action: AiAction;
  actionName: string;
};

function getStorage() {
  if (typeof window === "undefined") return null;
  return window.localStorage;
}

function buildConfirmationDescription(input: {
  actionName: string;
  chargedUnits: number;
  availableUnits: number;
  remainingUnits: number;
}) {
  return t`${input.actionName} requires ${input.chargedUnits} quota. You have ${input.availableUnits} available and will have ${input.remainingUnits} remaining after completion. Reserved quota is released if AI generation fails.`;
}

function buildInsufficientDescription(input: { actionName: string; chargedUnits: number; availableUnits: number }) {
  return t`${input.actionName} requires ${input.chargedUnits} quota, but only ${input.availableUnits} is available. Please go to billing or check quota status.`;
}

export function useQuotaPreflight() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const session = useAuthSession();

  const runWithQuotaPreflight = useCallback(
    async (options: QuotaPreflightRunOptions, run: () => Promise<void> | void) => {
      let preflight;
      try {
        preflight = await client.multiResumeQuota.preflight({ action: options.action });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t`Failed to check AI quota. Please try again.`);
        return false;
      }

      if (!preflight.allowed) {
        const goToBilling = await confirm(t`Insufficient AI quota`, {
          description: buildInsufficientDescription({
            actionName: options.actionName,
            chargedUnits: preflight.chargedUnits,
            availableUnits: preflight.availableUnits,
          }),
          confirmText: t`Go to billing`,
          cancelText: t`Cancel`,
        });

        if (goToBilling) void navigate({ to: "/dashboard/billing" });
        return false;
      }

      if (preflight.chargedUnits <= 0 || !preflight.requiresConfirmation) {
        await run();
        return true;
      }

      const storage = getStorage();
      const userId = session?.user.id;
      if (
        storage &&
        userId &&
        !shouldPromptForQuotaAction({
          storage,
          userId,
          action: options.action,
        })
      ) {
        await run();
        return true;
      }

      let skipConfirmation = false;
      const accepted = await confirm(t`Confirm AI quota usage?`, {
        description: buildConfirmationDescription({
          actionName: options.actionName,
          chargedUnits: preflight.chargedUnits,
          availableUnits: preflight.availableUnits,
          remainingUnits: preflight.remainingUnits,
        }),
        confirmText: t`Confirm and continue`,
        cancelText: t`Cancel`,
        checkboxLabel: t`Do not ask again for this action`,
        onCheckedChange: (checked) => {
          skipConfirmation = checked;
        },
      });

      if (!accepted) return false;

      if (skipConfirmation && storage && userId) {
        writeQuotaConfirmationPreference({
          storage,
          userId,
          actionClass: getQuotaConfirmationActionClass(options.action),
          skipConfirmation: true,
        });
      }

      await run();
      return true;
    },
    [confirm, navigate, session?.user.id],
  );

  return { runWithQuotaPreflight };
}
